import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AuthData } from "./auth";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEFAULT_USER_TIMEZONE = "Asia/Kolkata";

function isValidTimezone(tz: string): boolean {
  if (!tz || typeof tz !== "string") {
    return false;
  }

  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function resolveUserTimezone(tz?: string | null): string {
  if (tz && isValidTimezone(tz)) {
    return tz;
  }
  return DEFAULT_USER_TIMEZONE;
}

export function getUserTimezone(authData?: AuthData | null): string {
  if (authData?.tz && typeof authData.tz === "string") {
    return resolveUserTimezone(authData.tz);
  }

  const userContext = authData?.user_context;
  if (
    userContext &&
    typeof userContext === "object" &&
    typeof (userContext as { tz?: string }).tz === "string"
  ) {
    return resolveUserTimezone((userContext as { tz: string }).tz);
  }

  return DEFAULT_USER_TIMEZONE;
}

export const PICKING_FETCH_WINDOW_DAYS = 30;

export function getDefaultPickingWindow(tz: string): { start: Dayjs; end: Dayjs } {
  const safeTz = resolveUserTimezone(tz);
  const today = dayjs().tz(safeTz).startOf("day");

  return {
    start: today.subtract(PICKING_FETCH_WINDOW_DAYS - 1, "day"),
    end: today.endOf("day"),
  };
}

export function isWithinDefaultPickingWindow(date: Dayjs, tz: string): boolean {
  const { start, end } = getDefaultPickingWindow(tz);
  const selectedKey = date.format("YYYY-MM-DD");
  const startKey = start.format("YYYY-MM-DD");
  const endKey = end.format("YYYY-MM-DD");

  return selectedKey >= startKey && selectedKey <= endKey;
}

export function isDateRangeWithinDefaultPickingWindow(
  fromDate: Dayjs,
  toDate: Dayjs,
  tz: string
): boolean {
  const { start, end } = getDefaultPickingWindow(tz);
  const fromKey = fromDate.format("YYYY-MM-DD");
  const toKey = toDate.format("YYYY-MM-DD");
  const startKey = start.format("YYYY-MM-DD");
  const endKey = end.format("YYYY-MM-DD");

  return fromKey >= startKey && toKey <= endKey;
}

export function utcScheduledDateInLocalRange(
  utcValue: string | undefined,
  fromDate: Dayjs,
  toDate: Dayjs,
  tz: string
): boolean {
  if (!utcValue) {
    return false;
  }

  const safeTz = resolveUserTimezone(tz);
  const parsed = dayjs.utc(utcValue);
  if (!parsed.isValid()) {
    return false;
  }

  try {
    const key = parsed.tz(safeTz).format("YYYY-MM-DD");
    const fromKey = fromDate.format("YYYY-MM-DD");
    const toKey = toDate.format("YYYY-MM-DD");
    return key >= fromKey && key <= toKey;
  } catch {
    return false;
  }
}

export function localDateRangeToUtc(
  localDate: Dayjs,
  tz: string
): { startUtc: string; endUtc: string } {
  const safeTz = resolveUserTimezone(tz);
  const dateStr = localDate.format("YYYY-MM-DD");

  try {
    const startUtc = dayjs
      .tz(`${dateStr} 00:00:00`, safeTz)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = dayjs
      .tz(`${dateStr} 23:59:59`, safeTz)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    return { startUtc, endUtc };
  } catch {
    const startUtc = `${dateStr} 00:00:00`;
    const endUtc = `${dateStr} 23:59:59`;
    return { startUtc, endUtc };
  }
}

export function formatUtcScheduledDate(
  value: string | undefined,
  tz: string,
  format = "MM/DD/YYYY h:mm A"
): string {
  if (!value) {
    return "";
  }

  const safeTz = resolveUserTimezone(tz);
  const parsed = dayjs.utc(value);
  if (!parsed.isValid()) {
    return value;
  }

  try {
    return parsed.tz(safeTz).format(format);
  } catch {
    return parsed.format(format);
  }
}

export function utcScheduledDateMatchesLocalDay(
  utcValue: string | undefined,
  localDate: Dayjs,
  tz: string
): boolean {
  if (!utcValue) {
    return false;
  }

  const safeTz = resolveUserTimezone(tz);
  const parsed = dayjs.utc(utcValue);
  if (!parsed.isValid()) {
    return false;
  }

  try {
    return parsed.tz(safeTz).format("YYYY-MM-DD") === localDate.format("YYYY-MM-DD");
  } catch {
    return false;
  }
}
